import type { FileAsset, LinkAsset } from "@nlc/shared-types";

import { Button } from "@/components/button";

import { useAssetDownload } from "../hooks/useAssetBulkDownload.tsx";
import { useAssetSelection } from "../hooks/useAssetSelection.tsx";
import { isFileAsset } from "../utils/uploadUtils.ts";
import { DisplayOnlyAsset } from "./DisplayOnlyAsset.tsx";
import { DisplayOnlyLink } from "./DisplayOnlyLink.tsx";

type AssetDownloadButtonsProps = {
  hasSelectedAssets: boolean;
  onDeleteAll?: () => void;
  onDownloadAll: () => void;
  onDownloadSelected: () => void;
};

export function AssetDownloadButtons({
  hasSelectedAssets,
  onDeleteAll,
  onDownloadAll,
  onDownloadSelected,
}: AssetDownloadButtonsProps) {
  return (
    <div className="flex justify-between">
      <div className="gap-xl flex">
        <Button
          className="font-medium"
          color="gray"
          onPress={onDownloadAll}
          size="sm"
          variant="plain"
        >
          Download all
        </Button>
        <Button
          className="font-medium"
          isDisabled={!hasSelectedAssets}
          onPress={onDownloadSelected}
          size="sm"
          variant="tertiary"
        >
          Download selected
        </Button>
      </div>
      {onDeleteAll && (
        <Button
          className="font-medium"
          color="destructive"
          onPress={onDeleteAll}
          size="sm"
          variant="tertiary"
        >
          Delete all
        </Button>
      )}
    </div>
  );
}

type AssetListProps = {
  assets: Array<FileAsset | LinkAsset>;
  isAssetSelected: (attachmentId: number) => boolean;
  onAssetClick?: (assetId: string) => void;
  onAssetSelectionChange: (attachmentId: number, isSelected: boolean) => void;
};

export function AssetList({
  assets,
  isAssetSelected,
  onAssetClick,
  onAssetSelectionChange,
}: AssetListProps) {
  return (
    <div className="flex flex-col gap-xs mt-md">
      {assets.map((asset) =>
        isFileAsset(asset) ? (
          <DisplayOnlyAsset
            attachment={asset}
            handleChange={onAssetSelectionChange}
            isSelected={isAssetSelected(asset.attachmentId)}
            key={asset.id}
            onAssetClick={onAssetClick}
          />
        ) : (
          <DisplayOnlyLink
            createdAt={asset.createdAt}
            key={asset.id}
            url={asset.url || ""}
          />
        ),
      )}
    </div>
  );
}

type AssetDownloadSectionProps = {
  assets: Array<FileAsset | LinkAsset>;
  emptyMessage?: string;
  onAssetClick?: (assetId: string) => void;
  title?: string;
};

export function AssetDownloadSection({
  assets,
  emptyMessage = "No files available",
  onAssetClick,
  title,
}: AssetDownloadSectionProps) {
  const { handleAssetSelectionChange, isAssetSelected, selectedAssets } =
    useAssetSelection();

  const { handleDownloadAll, handleDownloadSelected } = useAssetDownload();

  const hasDownloadableFiles = assets.some((asset) => isFileAsset(asset));
  const isEmpty = assets.length === 0;

  return (
    <div className="flex flex-col gap-md">
      {title && (
        <span className="text-sm font-medium text-text-secondary">{title}</span>
      )}

      {isEmpty ? (
        <p className="py-xl text-center text-sm text-text-quaternary">
          {emptyMessage}
        </p>
      ) : (
        <>
          {hasDownloadableFiles && (
            <AssetDownloadButtons
              hasSelectedAssets={selectedAssets.length > 0}
              onDownloadAll={() => handleDownloadAll(assets)}
              onDownloadSelected={() => handleDownloadSelected(selectedAssets)}
            />
          )}

          <AssetList
            assets={assets}
            isAssetSelected={isAssetSelected}
            onAssetClick={onAssetClick}
            onAssetSelectionChange={handleAssetSelectionChange}
          />
        </>
      )}
    </div>
  );
}
